import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import FormContainer from "../../../components/FormContainer";
import User from '../../../models/User';
import Karyawan from '../../../models/Karyawan';
import Role from '../../../models/Role';
import { useStateContext } from '../../../reducers';
import DataForm from '../../../components/DataForm';
import { PageContainerActionParamType } from '../../../utils/Common';
import Loader from '../../../components/Loader';
import { DataFormFieldChangeParamType, SaveDataParamType } from '../../../utils/Types';
import { MenuCode, MenuGroupName, MenuTitle } from '../../../utils/Menu';
import { validate as uuidValidate } from "uuid";

const groupName: MenuGroupName = "Umum";
const subgroupName: string = "Pengaturan";
const code: MenuCode = "GLOBAL-USER";
const title: MenuTitle = "User";
const endpoint = "user";

function Form() {
	const location = useLocation();
    const {authState, setAuthState, apiCaller} = useStateContext();
    const [loading, setLoading] = useState(false);
	const [formData, setFormData] = useState<User>({is_new: true});
	const roleRef = useRef();
	const navigate = useNavigate();

    const isViewMode: boolean = useMemo(() => location?.state?.isViewMode ?? false, [location]);

	useEffect(() => {
		if (!location?.state?.ucode)
			return;

        setLoading(true);
		apiCaller?.get(`${endpoint}/id?ucode=${location.state.ucode}`)
            .then((response) => {
                const responseData: User | null = (response.data?.data as User) ?? null;

				const data: User | null = responseData ? {
					...responseData,
                    karyawan: responseData.ucode_karyawan ?
                        {
                            ucode: responseData.ucode_karyawan,
                            kode: responseData.kode_karyawan,
                            nama: responseData.nama_karyawan,
                        }
                        :
                        undefined,
                    role: responseData.ucode_role ?
                        {
                            ucode: responseData.ucode_role,
                            kode: responseData.kode_role,
                            nama: responseData.nama_role,
                        }
                        :
                        undefined,
					is_new: false,
				} 
				: 
				null;
				setFormData(data? {...data} : {is_new: true});			
			})
            .catch((error) => console.error({error}))
            .finally(() => {
				setLoading(false);
			})
        ;
	}, [location.state]);

    const onChanged = useCallback((valueObj: any) => {
        const {data, fieldName, value, isObject, keyFieldName, name} = valueObj;

        if (isObject && name && keyFieldName)
		{
            setFormData({
                ...formData,
                [fieldName]: value ? value[keyFieldName] : null,
                [name]: value,
            });
			return;
		}

		setFormData({
            ...data,
            [fieldName]: value,
        });
    }, [formData]);

    const onValidate = useCallback((data: User | undefined) => {
        if (!data?.ucode_karyawan)
            return false;

        if (!data?.ucode_role)
            return false;

        return true;
    }, [formData]);

    const onClose = useCallback((pathObject: PageContainerActionParamType | null | undefined) => {        
        navigate(`${pathObject?.mainPath}${pathObject?.path}`);
    }, [navigate]);

    const onSave = useCallback((param: SaveDataParamType<User> | undefined) => {
        if (!param?.data || !apiCaller)
            return;

        setLoading(true);

        const dataToSend = {
            ucode: param.data.ucode,
            ucode_role: param.data.ucode_role,
        };

        const apiCallerObj = apiCaller.put(`${endpoint}/update-role`, JSON.stringify(dataToSend));

        apiCallerObj
            .then(() => {
                setLoading(false);

                if (param.callback)
                    param.callback();
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller, formData]);

    return (
		<>
        {
            loading && <Loader />
        }
			<FormContainer
                groupName={groupName}
                subgroupName={subgroupName}
                code={code}
                title={title}
                editMode={formData?.ucode !== null && uuidValidate(formData?.ucode)}
                isViewMode={isViewMode}
            >
				<DataForm<User>
					groupName={groupName}
					subgroupName={subgroupName}
					code={code}
					title={title}
					data={formData}
					isViewMode={isViewMode}
					fields={[
						{
							name: "nama_karyawan",
							title: "Karyawan",
							inputType: "text",
							dataType: (typeof formData.nama_karyawan),
							isReadonly: true,
							isRequired: true,
						},
						{
							name: "username",
							title: "Username",
							inputType: "text",
							dataType: (typeof formData.username),
							isReadonly: true,
							isRequired: true,
						},
						{
							name: "role",
							keyField: "ucode",
							labelField: "nama",
							editKeyField: "ucode_role",
							title: "Role",
							inputType: "combobox",
							dataType: (typeof Role),
							isReadonly: false,
							isRequired: true,
							onChanged: (valueObj: DataFormFieldChangeParamType<User>) => onChanged(valueObj),
							searchEndpoint: "role/nama",
							searchEndpointFilterParamName: "nama",
							ref: roleRef,
						},				
					]}
					onValidate={onValidate}
					onClose={(pathObject) => onClose(pathObject)}
					onSave={(data) => onSave(data)}
				/>
			</FormContainer>
		</>
    );

};

export default Form;
